import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams } from 'react-router-dom'
import { FaBars, FaAngleLeft } from 'react-icons/fa'
import { FiUpload } from 'react-icons/fi'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import { apiURL, filesURL, sitePath } from 'config/globals'
import Topbar from 'components/Topbar'
import Sidebar from 'components/Sidebar'
import Breadcrumbs from 'components/Breadcrumbs'
import FormRow from 'components/FormRow'
import CancelButton from 'components/CancelButton'
import Footer from 'components/Footer'
import { checkAuth } from 'utils/Auth'
import ReactImagePickerEditor from 'react-image-picker-editor'
import 'react-image-picker-editor/dist/index.css'

const MuseumNew = () => {
    const [currentUser, setCurrentUser] = useState(false)
    const [museum, setMuseum] = useState({})
    const [form, setForm] = useState({})
    const [loadError, setLoadError] = useState('')
    const [submitError, setSubmitError] = useState('')
    const [issubmitting, setIsSubmitting] = useState(false)
    const navigate = useNavigate()
    const { id } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            getMuseum(id)
        }
        start()
    }, [])

    useEffect(() => {
        if (!museum) {
            return
        }

        setForm({
            ...form,
            modelType: museum.modelType
        })
    }, [museum])

    const getMuseum = async (id) => {
        const response = await axios.get(`${apiURL}/get-museum/${id}`)

        if (!response.data.success) {
            setLoadError("Cannot load museum data: " + (response.data.error || response.data))
            return
        }

        setMuseum(response.data.item)
    }

    const handleChange = (field, value) => {
        setForm({
            ...form,
            [field]: value
        })
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        event.stopPropagation()

        setSubmitError("")

        if (form.name === '' || form.description === '' || form.location === '') {
            setSubmitError("Please fill all required fields.")
            return
        }

        setIsSubmitting(true)
        
        try {
            const response = await axios.put(`${apiURL}/update-museum/${id}`, form)
            
            if (!response.data.success) {
                setSubmitError(response.data.error || response.data)
                setIsSubmitting(false)
                return
            }

            navigate(`/museums/${museum.id}`)
        } catch (error) {
            setSubmitError(JSON.stringify(error))
            setIsSubmitting(false)
        }
    }

    const cancel = () => {
        navigate(`/museums/${museum.id}`)
    }

    const back = () => {
        navigate(`/museums`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <Sidebar selectedItem="museums" currentUser={currentUser} />
            <main>
                <Breadcrumbs items={[['Museums', '/museums'], [museum.name, `/museums/${museum.id}`], ['Edit']]} />
                <div className="btn-toggle">
                    <FaBars />
                </div>
                <header>
                    <div className="float-start">
                        <h1>Edit Museum</h1>
                    </div>
                </header>
                {museum.id &&
                    <>
                        <FormRow
                            id="museumName"
                            label="Name"
                            required={true}
                            content={
                                <Form.Control
                                    name="name"
                                    defaultValue={museum.name}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            id="museumDescription"
                            label="Description"
                            required={true}
                            content={
                                <Form.Control
                                    name="description"
                                    as="textarea"
                                    rows={10}
                                    defaultValue={museum.description}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            id="museumLocation"
                            label="Location"
                            required={true}
                            content={
                                <Form.Control
                                    name="location"
                                    defaultValue={museum.location}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            id="mainImage"
                            label="Main Image"
                            required={true}
                            hint={<>Accepted types: JPG or PNG<br/>Recommended size: 1200x800</>}
                            content={
                                <div className="mb-3">
                                    <ReactImagePickerEditor
                                        config={{
                                            borderRadius: '8px',
                                            language: 'en',
                                            width: '330px',
                                            height: '250px',
                                            objectFit: 'contain',
                                            compressInitial: null,
                                        }}
                                        imageSrcProp={museum.mainImage
                                            ? `${filesURL}/${museum.mainImage}`
                                            : null
                                        }
                                        imageChanged={(dataUri) => {
                                            handleChange('mainImage', {
                                                uri: dataUri
                                            })
                                        }}
                                    />
                                </div>
                            }
                        />
                        {submitError && 
                            <Alert key='submitError' variant='danger' className="mt-4">
                                {submitError}
                            </Alert>
                        }
                        <div className="mt-5 mb-4 d-grid d-lg-block">
                            <CancelButton onClick={cancel} />
                            <Button
                                size="lg"
                                variant="primary"
                                className="float-end mb-3"
                                disabled={issubmitting}
                                onClick={handleSubmit}
                            >
                                {issubmitting
                                    ? <Spinner animation="border" size="sm" variant="secondary" className="mx-5" />
                                    : <><FiUpload /> Save</>
                                }
                            </Button>
                        </div>
                    </>
                }
                {loadError &&
                    <>
                        <Alert key='loadError' variant='danger'>
                            {loadError}
                        </Alert>
                        <div className="mt-5 mb-4 d-grid d-lg-block">
                            <Button variant="outline-primary" size="lg" className="mb-3" onClick={back}>
                                <FaAngleLeft /> Back
                            </Button>
                        </div>
                    </>
                }
                <Footer user={currentUser}  />
            </main>
        </>
    )
}

export default MuseumNew